import { Lock } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { StatusPill } from "@/components/StatusPill";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { plural } from "@/lib/plural";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import type { UserSummary } from "./shared";

const COLUMNS = [
  "User",
  "Role",
  "Teams",
  "Status",
  "Joined",
  "Last seen",
] as const;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 shrink-0 rounded-full bg-surface" />
              <div className="flex flex-col gap-1.5">
                <div className="h-3 w-32 rounded bg-surface" />
                <div className="h-2.5 w-40 rounded bg-surface" />
              </div>
            </div>
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-16 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-6 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-5 w-20 rounded-full bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function MemberUsers() {
  const { accessToken, user } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [error, setError] = useState<string | null>(null);

  const hasFilters = Boolean(debouncedNameFilter);

  const fetchUsers = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });

    if (debouncedNameFilter) {
      params.set("name", debouncedNameFilter);
    }

    return api.get<PagedResult<UserSummary>>(
      `/organizations/${orgId}/users?${params.toString()}`,
    );
  }, [accessToken, api, orgId, page, debouncedNameFilter]);

  const applyUsers = useCallback((data: PagedResult<UserSummary> | null) => {
    if (data) {
      setUsers(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } else {
      setError("Couldn't load users.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchUsers().then((data) => {
      if (!cancelled) {
        applyUsers(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchUsers, applyUsers]);

  function retry() {
    setError(null);
    setUsers(null);
    void fetchUsers().then(applyUsers);
  }

  function clearFilters() {
    setNameFilter("");
    setPage(1);
  }

  const isLoading = users === null && !error;
  const isEmpty = users !== null && totalCount === 0;

  return (
    <div className="text-foreground">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        {users !== null && (
          <p className="mt-0.5 text-sm text-foreground/70">
            {plural(totalCount, "user")}
          </p>
        )}
      </div>

      <div className="mb-4 flex gap-2">
        <SearchField
          value={nameFilter}
          onChange={(value) => {
            setNameFilter(value);
            setPage(1);
          }}
          label="Search users by name or email"
          placeholder="Search user name or email"
        />
      </div>

      {hasFilters && users !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(totalCount, "user")}</span>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {error && <LoadError what="users" onRetry={retry} className="mb-4" />}

      {isEmpty ? (
        <EmptyState
          title={hasFilters ? "No users match these filters" : "No users yet"}
          description={
            hasFilters
              ? "Try a different name."
              : "Your organization has no other members yet."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : (
                users?.map((u) => (
                  <tr
                    key={u.id}
                    className="h-14 border-b border-surface/70 hover:bg-surface"
                  >
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          name={u.name}
                          email={u.email}
                          avatarId={u.avatarId}
                          size="md"
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate">{u.name}</span>
                            {u.id === user?.id && <Chip>You</Chip>}
                            {u.isSsoManaged && (
                              <Chip>
                                <Lock className="h-2.5 w-2.5" strokeWidth={2} />
                                SSO
                              </Chip>
                            )}
                          </div>
                          <div className="truncate text-xs text-foreground/70">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-foreground/70">
                      {u.roles.join(", ") || "None"}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {u.teamCount}
                    </td>
                    <td className="py-2 pr-4">
                      {u.isSuspended ? (
                        <StatusPill variant="suspended">Suspended</StatusPill>
                      ) : (
                        <StatusPill variant="active">Active</StatusPill>
                      )}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDate(u.createdAt)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {u.lastSeenAt ? formatDate(u.lastSeenAt) : "Never"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={Math.ceil(totalCount / PAGE_SIZE)}
        onPageChange={setPage}
      />
    </div>
  );
}
